import React, { useState, useMemo } from 'react';
import {
  Brain,
  X,
  Trash2,
  Search,
  Building2,
  Layers,
  Wrench,
  ShieldCheck,
  Award,
  Truck,
  BookmarkCheck,
  Copy,
  Check,
  RotateCw,
  FolderPlus,
  Database
} from 'lucide-react';

/**
 * Helper to select an icon corresponding to group name
 */
function getGroupIcon(groupName = '') {
  const g = groupName.toLowerCase();
  if (['company', 'firm', 'organization'].some((w) => g.includes(w))) return Building2;
  if (['program', 'product', 'project', 'assembly'].some((w) => g.includes(w))) return Layers;
  if (['tool', 'software', 'cad', 'cam'].some((w) => g.includes(w))) return Wrench;
  if (['material', 'metal', 'grade', 'alloy'].some((w) => g.includes(w))) return ShieldCheck;
  if (['code', 'standard', 'spec', 'compliance'].some((w) => g.includes(w))) return Award;
  if (['supplier', 'vendor', 'fab', 'lead'].some((w) => g.includes(w))) return Truck;
  if (['decision', 'history', 'rationale'].some((w) => g.includes(w))) return BookmarkCheck;
  return Brain;
}

export default function ProjectMemoryDrawer({
  isOpen,
  onClose,
  memoryStatements = [],
  onAddStatement,
  onDeleteStatement,
  onRefresh,
  _activeThreadId = null
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Form states matching Group, Subtitle, Memory Content
  const [newGroup, setNewGroup] = useState('Company');
  const [newSubtitle, setNewSubtitle] = useState('');
  const [newMemoryContent, setNewMemoryContent] = useState('');

  // Filter backend statements by search query across group, subtitle, or memory content
  const filteredStatements = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return memoryStatements;
    return memoryStatements.filter((stmt) => {
      const sub = (stmt.subtitle || stmt.memory_key || stmt.key || '').toLowerCase();
      const content = (stmt.memory_content || stmt.memory_context || stmt.content || stmt.text || '').toLowerCase();
      const grp = (stmt.group || '').toLowerCase();
      return sub.includes(q) || content.includes(q) || grp.includes(q);
    });
  }, [memoryStatements, searchQuery]);

  // Group everything by group together
  const groupedMemory = useMemo(() => {
    const groups = {};
    for (const item of filteredStatements) {
      const grp = (item.group || 'General').trim();
      if (!groups[grp]) groups[grp] = [];
      groups[grp].push(item);
    }
    return groups;
  }, [filteredStatements]);

  const groupKeys = Object.keys(groupedMemory).sort((a, b) => {
    if (a.toLowerCase() === 'company') return -1;
    if (b.toLowerCase() === 'company') return 1;
    return a.localeCompare(b);
  });

  // Copy helper
  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy memory:', err);
    }
  };

  // Manual refresh helper
  const handleManualRefresh = async () => {
    if (!onRefresh) return;
    setIsRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setTimeout(() => setIsRefreshing(false), 400);
    }
  };

  // Add memory helper
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!newSubtitle.trim() && !newMemoryContent.trim()) return;
    if (onAddStatement) {
      await onAddStatement({
        group: newGroup.trim() || 'General',
        subtitle: newSubtitle.trim(),
        memory_content: newMemoryContent.trim(),
        memory_key: newSubtitle.trim(),
        memory_context: newMemoryContent.trim()
      });
    }
    setNewSubtitle('');
    setNewMemoryContent('');
    setIsAdding(false);
  };

  if (!isOpen) return null;

  // Copy entire backend memory in formatted Markdown
  const handleCopyAll = () => {
    const lines = [`# Dashnode Memory Knowledge\n`];
    for (const grpName of groupKeys) {
      lines.push(`## Group: ${grpName}`);
      for (const it of groupedMemory[grpName]) {
        const sub = it.subtitle || it.memory_key || it.key || 'Note';
        const content = it.memory_content || it.memory_context || it.content || it.text || '';
        lines.push(`- **${sub}**: ${content}`);
      }
      lines.push('');
    }
    copyToClipboard(lines.join('\n'), 'all');
  };

  return (
    <aside className="w-[332px] shrink-0 h-full bg-white border-l border-slate-200 flex flex-col shadow-lg z-20 font-sans select-text">
      {/* 1. Header */}
      <div className="p-4 pb-3 border-b border-slate-200 shrink-0 bg-white select-none">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-[#EFEAFB] text-[#6D53D6] flex items-center justify-center shrink-0">
              <Brain className="w-4 h-4" />
            </div>
            <div className="flex items-center gap-1.5">
              <b className="text-sm font-semibold text-slate-900">Memory</b>
              <span className="text-[11px] font-mono px-1.5 py-0.2 rounded-full bg-purple-50 text-purple-700 font-semibold border border-purple-200">
                {memoryStatements.length}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1 text-slate-400">
            {/* Refresh Button */}
            {onRefresh && (
              <button
                type="button"
                onClick={handleManualRefresh}
                title="Refresh from backend"
                className="p-1 hover:text-slate-700 rounded-md hover:bg-slate-100 transition-colors cursor-pointer"
              >
                <RotateCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin text-[#0C64E8]' : ''}`} />
              </button>
            )}

            {/* Copy All Button */}
            <button
              type="button"
              onClick={handleCopyAll}
              title={copiedId === 'all' ? 'Copied all to clipboard!' : 'Copy all memory'}
              className="px-2 py-1 hover:text-slate-700 rounded-md hover:bg-slate-100 transition-colors cursor-pointer flex items-center gap-1 text-[11px] font-medium"
            >
              {copiedId === 'all' ? (
                <>
                  <Check className="w-3 h-3 text-emerald-600" />
                  <span className="text-emerald-600 font-semibold">Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" />
                  <span className="text-slate-600">Copy all</span>
                </>
              )}
            </button>

            {/* Close Button */}
            <button
              type="button"
              onClick={onClose}
              title="Close panel"
              className="p-1 hover:text-slate-700 rounded-md hover:bg-slate-100 transition-colors cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <p className="text-[11px] text-[#8193A8] mt-1.5 leading-snug">
          Live engineering knowledge loaded dynamically from backend.
        </p>

        {/* Filter Search Input */}
        <div className="mt-3 relative">
          <Search className="w-3.5 h-3.5 text-[#8193A8] absolute left-2.5 top-2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search group, subtitle, or content..."
            className="w-full pl-8 pr-2.5 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-800 placeholder-slate-400 outline-none focus:border-[#0C64E8] focus:bg-white transition-all"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1.5 text-slate-400 hover:text-slate-700 text-xs font-bold"
            >
              ×
            </button>
          )}
        </div>

        {/* Add Memory Toggle Button */}
        <div className="mt-2.5 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setIsAdding(!isAdding)}
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#0C64E8] hover:text-[#0A4FBB] transition-colors cursor-pointer"
          >
            <FolderPlus className="w-3.5 h-3.5" />
            <span>{isAdding ? 'Cancel adding' : '+ Add memory entry'}</span>
          </button>
        </div>

        {/* Add Memory Form */}
        {isAdding && (
          <form onSubmit={handleAddSubmit} className="mt-2.5 p-2.5 bg-slate-50 border border-slate-200 rounded-xl space-y-2 text-xs">
            <div>
              <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-0.5">
                Group
              </label>
              <input
                type="text"
                value={newGroup}
                onChange={(e) => setNewGroup(e.target.value)}
                placeholder="e.g. Company, Past Decisions, Materials"
                className="w-full px-2 py-1 bg-white border border-slate-200 rounded-md text-xs outline-none focus:border-[#0C64E8]"
                list="group-suggestions"
              />
              <datalist id="group-suggestions">
                <option value="Company" />
                <option value="Programs & Products" />
                <option value="Tools in Use" />
                <option value="Qualified Materials" />
                <option value="Codes & Standards" />
                <option value="Qualified Fab Suppliers" />
                <option value="Past Decisions" />
                <option value="Shop Rules" />
              </datalist>
            </div>

            <div>
              <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-0.5">
                Subtitle
              </label>
              <input
                type="text"
                value={newSubtitle}
                onChange={(e) => setNewSubtitle(e.target.value)}
                placeholder="e.g. Firm, Gen-2 core wall, Tolerance"
                className="w-full px-2 py-1 bg-white border border-slate-200 rounded-md text-xs outline-none focus:border-[#0C64E8]"
              />
            </div>

            <div>
              <label className="text-[10px] font-semibold uppercase text-slate-500 block mb-0.5">
                Memory Content
              </label>
              <textarea
                rows={2}
                value={newMemoryContent}
                onChange={(e) => setNewMemoryContent(e.target.value)}
                placeholder="e.g. Kaveri Nuclear Systems · Pune, ±0.05 mm"
                className="w-full px-2 py-1 bg-white border border-slate-200 rounded-md text-xs outline-none focus:border-[#0C64E8] resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={!newSubtitle.trim() && !newMemoryContent.trim()}
              className="w-full py-1.5 bg-[#0C64E8] hover:bg-[#0A4FBB] disabled:opacity-40 text-white font-semibold rounded-md text-xs transition-colors cursor-pointer"
            >
              Save Memory
            </button>
          </form>
        )}
      </div>

      {/* 2. Scrollable Body: Live Backend Memory Grouped Together by Group */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-5 text-xs select-text">
        {groupKeys.length === 0 ? (
          <div className="py-12 text-center text-slate-400 space-y-2 select-none">
            <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-400 flex items-center justify-center mx-auto">
              <Database className="w-5 h-5" />
            </div>
            <b className="text-xs font-semibold text-slate-600 block">
              {searchQuery ? 'No matching memory records' : 'No memory records in backend'}
            </b>
            <p className="text-[11px] text-slate-400 max-w-[24ch] mx-auto leading-relaxed">
              {searchQuery
                ? 'Try a different search keyword.'
                : 'Memory records will load from the backend or can be added using the button above.'}
            </p>
          </div>
        ) : (
          groupKeys.map((groupName) => {
            const items = groupedMemory[groupName];
            const GroupIcon = getGroupIcon(groupName);

            // Group text for "copy group"
            const groupText = items
              .map((it) => `${it.subtitle || it.memory_key}: ${it.memory_content || it.memory_context}`)
              .join('\n');

            return (
              <div key={groupName} className="space-y-2 select-text">
                {/* Group Header: Group everything by group together */}
                <div className="flex items-center justify-between group/grp border-b border-slate-100 pb-1.5 select-none">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                    <GroupIcon className="w-3.5 h-3.5 text-[#0C64E8]" />
                    <span>{groupName}</span>
                    <span className="text-[10px] font-mono font-medium px-1.5 py-0.2 rounded-full bg-slate-100 text-slate-500 ml-1">
                      {items.length}
                    </span>
                  </span>

                  {/* Copy entire group button */}
                  <button
                    type="button"
                    onClick={() => copyToClipboard(groupText, `grp-${groupName}`)}
                    className="opacity-0 group-hover/grp:opacity-100 p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-all cursor-pointer"
                    title={`Copy all ${groupName} items`}
                  >
                    {copiedId === `grp-${groupName}` ? (
                      <Check className="w-3 h-3 text-emerald-600" />
                    ) : (
                      <Copy className="w-3 h-3" />
                    )}
                  </button>
                </div>

                {/* Group Items List: Each item displays Subtitle and Memory Content */}
                <div className="space-y-2 select-text">
                  {items.map((it) => {
                    const itemId = it.id || it.key || it.subtitle || it.memory_key;
                    const sub = it.subtitle || it.memory_key || it.key || 'Note';
                    const content = it.memory_content || it.memory_context || it.content || it.text || '';
                    const itemCopyText = `${sub}: ${content}`;

                    return (
                      <div
                        key={itemId}
                        className="group relative border border-slate-200/80 bg-white hover:border-[#C6D6EE] hover:bg-[#FBFDFF] rounded-lg p-2.5 transition-all select-text shadow-xs"
                      >
                        {/* Subtitle row with action buttons */}
                        <div className="flex items-start justify-between gap-2 select-text">
                          <b className="text-[12px] font-semibold text-slate-900 leading-snug select-text cursor-text">
                            {sub}
                          </b>

                          <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            {/* Copy item */}
                            <button
                              type="button"
                              onClick={() => copyToClipboard(itemCopyText, itemId)}
                              className="p-1 text-slate-400 hover:text-[#0C64E8] hover:bg-slate-100 rounded transition-colors cursor-pointer"
                              title="Copy item"
                            >
                              {copiedId === itemId ? (
                                <Check className="w-3 h-3 text-emerald-600" />
                              ) : (
                                <Copy className="w-3 h-3" />
                              )}
                            </button>

                            {/* Delete item */}
                            {onDeleteStatement && (
                              <button
                                type="button"
                                onClick={() => onDeleteStatement(it.key || it.id, it.scope)}
                                className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
                                title="Delete from backend"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Memory Content row */}
                        <p className="text-[12px] text-slate-600 mt-1 leading-relaxed select-text cursor-text break-words">
                          {content}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </aside>
  );
}
